import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, BadgeCheck, Star, MapPin, LogOut, ShoppingBag, Umbrella, Clock, MessageCircle } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import { ISLANDS, ISLAND_NAME } from "../lib/data";
import api, { fileUrl, formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import BusinessPlan from "../components/BusinessPlan"; // eslint-disable-line no-unused-vars
import NotificationSettings from "../components/NotificationSettings";
import ReviewsModal from "../components/ReviewsModal";
import { toast } from "sonner";

export default function Profile() {
  const { t, lang } = useI18n();
  const { user, setUser, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const avatarRef = useRef();
  const [form, setForm] = useState(null);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [vacBusy, setVacBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleVacation = async () => {
    setVacBusy(true);
    try {
      const { data: u } = await api.post("/seller/vacation", { on: !user?.on_vacation });
      setUser(u);
      toast.success(u.on_vacation ? t("dashboard.vacationOn") : t("dashboard.vacationOff"));
    } catch { toast.error("Error"); } finally { setVacBusy(false); }
  };

  useEffect(() => { if (user === false) navigate("/auth"); }, [user, navigate]);
  useEffect(() => {
    if (user) setForm({ name: user.name, nickname: user.nickname || "", bio: user.bio || "", island: user.island || "", municipality: user.municipality || "",
      profile_photo: user.profile_photo, is_professional: user.is_professional, business_name: user.business_name || "",
      website: user.website || "", phone: user.phone || "",
      whatsapp_number: user.whatsapp_number || "", whatsapp_visible: user.whatsapp_visible ?? false, bizum_number: user.bizum_number || "", paypal_account: user.paypal_account || "" });
  }, [user]);

  if (!user || !form) return <div className="p-10 text-center text-slate-400">{t("common.loading")}</div>;
  const munis = ISLANDS.find((i) => i.id === form.island)?.municipalities || [];

  const uploadAvatar = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("profile_photo", data.path); }
    catch { toast.error("Error"); }
  };

  const save = async () => {
    try { const { data } = await api.put("/auth/me", form); setUser(data); toast.success(t("common.saved")); navigate("/"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const becomePro = async () => {
    try { const { data } = await api.put("/auth/me", { ...form, is_professional: true }); setUser(data); set("is_professional", true); toast.success(t("monetization.proBadge")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  void becomePro;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
      {/* Header card */}
      <div className="bg-gradient-to-br from-[#008080] to-[#0F2537] rounded-3xl p-5 sm:p-6 text-white flex flex-col sm:flex-row items-center sm:items-start gap-4 mb-6">
        <div className="relative shrink-0">
          <Avatar className="h-20 w-20 border-2 border-white/30"><AvatarImage src={fileUrl(form.profile_photo)} /><AvatarFallback className="bg-teal-700 text-white text-2xl">{user.name?.[0]}</AvatarFallback></Avatar>
          <button data-testid="avatar-upload-btn" onClick={() => avatarRef.current?.click()} className="absolute -bottom-1 -right-1 h-8 w-8 grid place-items-center rounded-full bg-white text-[#008080] shadow"><Camera className="h-4 w-4" /></button>
          <input ref={avatarRef} type="file" accept="image/*" hidden onChange={uploadAvatar} />
        </div>
        <div className="flex-1 min-w-0 w-full text-center sm:text-left">
          <h1 className="font-display font-extrabold text-2xl flex items-center justify-center sm:justify-start gap-2 break-words">{user.nickname ? `@${user.nickname}` : (user.municipality || "—")}{user.is_professional && <BadgeCheck className="h-5 w-5 shrink-0" />}</h1>
          <p className="text-teal-100 text-sm flex items-center justify-center sm:justify-start gap-1 mt-1 break-words"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0">{user.municipality || "—"}{user.island ? `, ${ISLAND_NAME[user.island]}` : ""}</span></p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm w-full">
            <button data-testid="profile-rating-btn" onClick={() => setReviewsOpen(true)} className="flex items-start gap-1 hover:text-amber-200 transition-colors cursor-pointer min-w-0 text-left"><Star className="h-3.5 w-3.5 fill-amber-300 text-amber-300 shrink-0 mt-0.5" /><span className="leading-snug break-words">{user.rating || 0} <span className="text-teal-200">({user.review_count || 0}) {t("profile.reviews")}</span></span></button>
            <span className="flex items-start gap-1 min-w-0"><span className="leading-snug break-words"><b>{user.followers?.length || 0}</b> {t("profile.followers")}</span></span>
            <span data-testid="profile-sold-count" className="flex items-start gap-1 min-w-0"><ShoppingBag className="h-3.5 w-3.5 text-teal-200 shrink-0 mt-0.5" /><span className="leading-snug break-words"><b>{user.sold_count || 0}</b> {t("profile.sold")}</span></span>
            <span className="flex items-start gap-1 text-teal-200 text-xs min-w-0"><span className="leading-snug break-words">{t("profile.memberSince")} {formatDate(user.created_at, lang)}</span></span>
          </div>
        </div>
      </div>

      <Tabs defaultValue="account">
        <TabsList className="mb-4">
          <TabsTrigger value="account" data-testid="tab-account">{t("profile.myAccount")}</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">{t("profile.notifications")}</TabsTrigger>
          <TabsTrigger value="pro" data-testid="tab-pro">{t("profile.professional")}</TabsTrigger>
        </TabsList>

        <TabsContent value="account">
          <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-7 space-y-5">
            <div>
              <Label>{t("profile.nickname")}</Label>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-slate-400 text-sm">@</span>
                <Input data-testid="profile-nickname-input" value={form.nickname} onChange={(e) => set("nickname", e.target.value)} disabled={user.nickname_changed} className="flex-1" />
              </div>
              <p className={`text-xs mt-1 ${user.nickname_changed ? "text-amber-600" : "text-slate-400"}`}>
                {user.nickname_changed ? t("profile.nicknameLocked") : t("profile.nicknameNote")}
              </p>
            </div>
            <div><Label>{t("profile.bio")}</Label><Textarea data-testid="profile-bio-input" value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={3} className="mt-1" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>{t("onboarding.island")}</Label>
                <Select value={form.island} onValueChange={(v) => { set("island", v); set("municipality", ""); }}>
                  <SelectTrigger data-testid="profile-island-select" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{ISLANDS.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("onboarding.municipality")}</Label>
                <Select value={form.municipality} onValueChange={(v) => set("municipality", v)} disabled={!form.island}>
                  <SelectTrigger data-testid="profile-muni-select" className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{munis.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-5" data-testid="profile-whatsapp-panel">
              <Label className="flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-emerald-600" />{t("profile.whatsappNumber")}</Label>
              <Input data-testid="profile-whatsapp-input" value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="+34 600 000 000" className="mt-1" />
              <p className="text-xs text-slate-400 mt-1">{t("profile.whatsappHint")}</p>
              <label className="mt-3 flex items-center justify-between gap-3 cursor-pointer">
                <span className="text-sm text-slate-600">{t("profile.whatsappVisible")}</span>
                <Switch data-testid="profile-whatsapp-visible" checked={!!form.whatsapp_visible} onCheckedChange={(v) => set("whatsapp_visible", v)} />
              </label>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-sm flex items-center gap-1.5"><Umbrella className="h-4 w-4 text-amber-600" />{user.on_vacation ? t("dashboard.reopenShop") : t("dashboard.vacationShop")}</p>
                <p className="text-xs text-slate-500 mt-0.5">{user.on_vacation ? t("dashboard.vacationBanner") : t("profile.vacationHint")}</p>
              </div>
              <Button data-testid="profile-vacation-btn" onClick={toggleVacation} disabled={vacBusy}
                className={user.on_vacation ? "bg-emerald-500 hover:bg-emerald-600 shrink-0" : "bg-amber-500 hover:bg-amber-600 shrink-0"}>
                {user.on_vacation ? t("dashboard.reopenShop") : t("dashboard.vacationShop")}
              </Button>
            </div>
            <div className="flex gap-3">
              <Button data-testid="save-profile-btn" onClick={save} className="bg-[#008080] hover:bg-[#006666]">{t("common.save")}</Button>
              <Button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }} variant="outline"><LogOut className="h-4 w-4 mr-1" />{t("profile.disconnect")}</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationSettings />
        </TabsContent>

        <TabsContent value="pro">
          <div className="rounded-2xl border border-slate-200 p-5" data-testid="profile-plan-panel">
            <p className="font-display font-bold text-lg">{t("nav.plan")}</p>
            <p className="text-sm text-slate-500 mt-1">
              {lang === "it" ? "Piano attuale" : lang === "en" ? "Current plan" : "Plan actual"}:{" "}
              <b className="text-slate-800 capitalize">{user.plan || "free"}</b>
              {user.plan_status === "pending_payment" && <span className="ml-2 text-amber-600 text-xs">({lang === "it" ? "pagamento in sospeso" : lang === "en" ? "payment pending" : "pago pendiente"})</span>}
            </p>
            <Button data-testid="profile-manage-plan-btn" onClick={() => navigate("/planes")} className="mt-4 bg-[#008080] hover:bg-[#006666]">
              {lang === "it" ? "Gestisci piano" : lang === "en" ? "Manage plan" : "Gestionar plan"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <ReviewsModal open={reviewsOpen} onOpenChange={setReviewsOpen} userId={user.id}
        name={user.name} rating={user.rating} count={user.review_count} />
    </div>
  );
}
